import { useState, useEffect, useCallback, useRef } from 'react';

const API = (process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001') + '/api/media';

// ── API helpers ───────────────────────────────────────────
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('mh_token')}`,
});

const mediaAPI = {
  list:   (p) => fetch(`${API}?${new URLSearchParams(p)}`, { headers: authHeaders() }).then(r => r.json()),
  upload: (file, alt) => {
    const fd = new FormData();
    fd.append('file', file);
    if (alt) fd.append('alt_text', alt);
    return fetch(`${API}/upload`, { method: 'POST', headers: authHeaders(), body: fd }).then(r => r.json());
  },
  remove: (id) => fetch(`${API}/${id}`, { method: 'DELETE', headers: authHeaders() }).then(r => r.json()),
  update: (id, d) => fetch(`${API}/${id}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(d),
  }).then(r => r.json()),
  stats: () => fetch(`${API}/stats`, { headers: authHeaders() }).then(r => r.json()),
};

const ACCEPT = 'image/*,video/mp4,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';
const TYPE_ICON  = { image: '◈', video: '▶', document: '◻' };
const TYPE_COLOR = { image: 'var(--accent)', video: 'var(--blue-light)', document: 'var(--green-light)' };

function formatSize(b) {
  if (!b) return '—';
  if (b < 1024)       return `${b} B`;
  if (b < 1024*1024)  return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
}

// ── Upload zone ───────────────────────────────────────────
function UploadZone({ onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads]   = useState([]);
  const inputRef = useRef();

  const processFiles = async (files) => {
    const list  = Array.from(files);
    const items = list.map(f => ({
      id: Math.random().toString(36).slice(2),
      name: f.name, progress: 0, status: 'uploading', file: f,
    }));
    setUploads(prev => [...prev, ...items]);

    for (const item of items) {
      try {
        const ticker = setInterval(() => {
          setUploads(prev => prev.map(u =>
            u.id === item.id && u.progress < 85 ? { ...u, progress: u.progress + 12 } : u
          ));
        }, 180);
        const result = await mediaAPI.upload(item.file);
        clearInterval(ticker);
        if (result.data) {
          setUploads(prev => prev.map(u =>
            u.id === item.id ? { ...u, progress: 100, status: 'done' } : u
          ));
          onUploaded(result.data);
          setTimeout(() => setUploads(prev => prev.filter(u => u.id !== item.id)), 2000);
        } else {
          setUploads(prev => prev.map(u =>
            u.id === item.id ? { ...u, status: 'error', error: result.error || 'Upload failed' } : u
          ));
        }
      } catch (err) {
        setUploads(prev => prev.map(u =>
          u.id === item.id ? { ...u, status: 'error', error: err.message } : u
        ));
      }
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); processFiles(e.dataTransfer.files); };
  const onPaste = useCallback((e) => {
    if (e.clipboardData?.files?.length) processFiles(e.clipboardData.files);
  }, []);
  useEffect(() => {
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onPaste]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-lg)', padding: '36px 24px',
          textAlign: 'center', cursor: 'pointer',
          background: dragging ? 'var(--accent-glow)' : 'var(--bg-surface)',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.5 }}>⬆</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          Drop files here or click to browse
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          Images · Videos · Documents &nbsp;·&nbsp; You can also <strong style={{ color: 'var(--accent)' }}>paste</strong> from clipboard
        </div>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
          onChange={e => processFiles(e.target.files)} />
      </div>

      {uploads.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {uploads.map(u => (
            <div key={u.id} style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ fontSize: 16 }}>
                {u.status === 'done' ? '✓' : u.status === 'error' ? '✕' : '↑'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                {u.status === 'error' && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>{u.error}</div>}
                {u.status === 'uploading' && (
                  <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${u.progress}%`, background: 'var(--accent)', transition: 'width 0.2s ease', borderRadius: 2 }} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: u.status === 'done' ? 'var(--green-light)' : u.status === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>
                {u.status === 'done' ? 'Done' : u.status === 'error' ? 'Failed' : `${u.progress}%`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Asset card (grid) ─────────────────────────────────────
function AssetCard({ asset, onSelect, onDelete }) {
  const [hover, setHover] = useState(false);
  const isImg    = asset.type === 'image';
  const isVid    = asset.type === 'video';
  const thumbUrl = asset.thumbnail_url || (isImg ? asset.file_url : null);

  return (
    <div onClick={() => onSelect(asset)}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${hover ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer',
        background: 'var(--bg-surface)', transition: 'all 0.15s',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? 'var(--shadow-md)' : 'none',
      }}>
      <div style={{ height: 120, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {thumbUrl
          ? <img src={thumbUrl} alt={asset.alt_text || asset.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
          : <span style={{ fontSize: 32, opacity: 0.3, color: TYPE_COLOR[asset.type] }}>{TYPE_ICON[asset.type] || '◻'}</span>
        }
        {isVid && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
            <span style={{ fontSize: 22, color: '#fff' }}>▶</span>
          </div>
        )}
        {hover && (
          <button onClick={e => { e.stopPropagation(); onDelete(asset); }} style={{
            position: 'absolute', top: 6, right: 6, background: 'rgba(240,96,96,0.85)', color: '#fff',
            border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: 11,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        )}
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
          {asset.alt_text || asset.filename}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: TYPE_COLOR[asset.type], background: TYPE_COLOR[asset.type] + '20', padding: '1px 6px', borderRadius: 8, fontWeight: 600, textTransform: 'uppercase' }}>{asset.type}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatSize(asset.size_bytes)}</span>
        </div>
      </div>
    </div>
  );
}

// ── List row thumbnail ────────────────────────────────────
function ListThumb({ asset }) {
  const thumbUrl = asset.thumbnail_url || (asset.type === 'image' ? asset.file_url : null);
  return (
    <div style={{
      width: 44, height: 44, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {thumbUrl ? (
        <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
      ) : (
        <span style={{ fontSize: 20, color: TYPE_COLOR[asset.type], opacity: 0.6 }}>
          {TYPE_ICON[asset.type] || '◻'}
        </span>
      )}
    </div>
  );
}

// ── Asset detail modal ────────────────────────────────────
function AssetDetail({ asset, onClose, onDelete, onUpdate }) {
  const [alt, setAlt]       = useState(asset.alt_text || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyUrl = () => { navigator.clipboard.writeText(asset.file_url); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const save = async () => {
    setSaving(true);
    await mediaAPI.update(asset.id, { alt_text: alt });
    setSaving(false);
    onUpdate({ ...asset, alt_text: alt });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: 580, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', animation: 'fadeIn 0.2s ease' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Asset Details</span>
          <button onClick={onClose} style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--bg-elevated)', border: '1px solid var(--border)', maxHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {asset.type === 'image' && <img src={asset.file_url} alt={asset.alt_text} style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }} />}
            {asset.type === 'video' && <video controls style={{ maxWidth: '100%', maxHeight: 280 }} poster={asset.poster_url || undefined}><source src={asset.file_url} type={asset.mime_type} /></video>}
            {asset.type === 'document' && <div style={{ padding: 40, textAlign: 'center' }}><div style={{ fontSize: 48, color: 'var(--green-light)', marginBottom: 10 }}>◻</div><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{asset.filename}</div></div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 14, border: '1px solid var(--border)' }}>
            {[['Filename', asset.filename], ['Type', asset.mime_type || asset.type], ['Size', formatSize(asset.size_bytes)], ['Dimensions', asset.width ? `${asset.width} × ${asset.height}px` : '—'], ['Uploaded', asset.created_at ? new Date(asset.created_at).toLocaleString() : '—'], ['By', asset.uploader_name || '—']].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: 12, wordBreak: 'break-all' }}>{v}</div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>File URL</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input readOnly value={asset.file_url} style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', padding: '8px 12px', fontSize: 11, fontFamily: 'monospace' }} />
              <button onClick={copyUrl} style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: copied ? 'var(--green-muted)' : 'var(--bg-elevated)', color: copied ? 'var(--green-light)' : 'var(--text-secondary)', border: `1px solid ${copied ? 'var(--green-light)' : 'var(--border)'}`, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>{copied ? '✓ Copied' : 'Copy URL'}</button>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Alt Text / Label</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={alt} onChange={e => setAlt(e.target.value)} style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '8px 12px', fontSize: 13 }} />
              <button onClick={save} disabled={saving} style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--accent)', color: '#0d0f12', border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{saving ? '…' : 'Save'}</button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', paddingTop: 4 }}>
            <button onClick={() => { onDelete(asset); onClose(); }} style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--red-muted)', color: 'var(--red-light)', border: 'none', fontSize: 12, cursor: 'pointer' }}>Delete File</button>
            <a href={asset.file_url} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', fontSize: 12 }}>Open Original ↗</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function MediaLibraryPage() {
  const [assets, setAssets]         = useState([]);
  const [pagination, setPag]        = useState(null);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState('grid');
  const [filter, setFilter]         = useState({ type: '', page: 1, search: '' });
  const [selected, setSelected]     = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 24, page: filter.page };
      if (filter.type)   params.type   = filter.type;
      if (filter.search) params.search = filter.search;
      const res = await mediaAPI.list(params);
      setAssets(res.data || []); setPag(res.pagination);
    } catch { setAssets([]); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { mediaAPI.stats().then(r => setStats(r.data)).catch(() => {}); }, [assets.length]);

  const handleSearch = (val) => { clearTimeout(searchTimer.current); searchTimer.current = setTimeout(() => setFilter(f => ({ ...f, search: val, page: 1 })), 350); };
  const handleDelete = async (asset) => { if (!window.confirm(`Delete "${asset.alt_text || asset.filename}"?`)) return; await mediaAPI.remove(asset.id); setAssets(prev => prev.filter(a => a.id !== asset.id)); };
  const handleUpdate = (updated) => { setAssets(prev => prev.map(a => a.id === updated.id ? updated : a)); setSelected(updated); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { label: 'Total Files', value: stats.total_files,  sub: stats.total_formatted,    color: 'var(--text-secondary)' },
            { label: 'Images',      value: stats.images,       sub: stats.image_formatted,    color: 'var(--accent)' },
            { label: 'Videos',      value: stats.videos,       sub: stats.video_formatted,    color: 'var(--blue-light)' },
            { label: 'Documents',   value: stats.documents,    sub: stats.document_formatted, color: 'var(--green-light)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value ?? '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input placeholder="Search files…" onChange={e => handleSearch(e.target.value)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '8px 14px', fontSize: 13, width: 220 }} />
        {['', 'image', 'video', 'document'].map(t => (
          <button key={t} onClick={() => setFilter(f => ({ ...f, type: t, page: 1 }))} style={{ padding: '7px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer', background: filter.type === t ? 'var(--accent)' : 'var(--bg-elevated)', color: filter.type === t ? '#0d0f12' : 'var(--text-secondary)', border: `1px solid ${filter.type === t ? 'var(--accent)' : 'var(--border)'}`, fontWeight: filter.type === t ? 600 : 400, textTransform: 'capitalize' }}>{t || 'All'}</button>
        ))}
        <div style={{ flex: 1 }} />
        {['grid', 'list'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '7px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13, background: view === v ? 'var(--accent-muted)' : 'var(--bg-elevated)', color: view === v ? 'var(--accent)' : 'var(--text-muted)', border: `1px solid ${view === v ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer' }}>
            {v === 'grid' ? '⊞ Grid' : '☰ List'}
          </button>
        ))}
        <button onClick={() => setShowUpload(v => !v)} style={{ padding: '8px 16px', borderRadius: 'var(--radius-sm)', background: showUpload ? 'var(--accent-muted)' : 'var(--accent)', color: showUpload ? 'var(--accent)' : '#0d0f12', border: `1px solid var(--accent)`, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {showUpload ? '✕ Close' : '⬆ Upload'}
        </button>
      </div>

      {showUpload && <UploadZone onUploaded={(a) => setAssets(prev => [a, ...prev])} />}

      {/* Grid */}
      {view === 'grid' && (
        loading
          ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading…</div>
          : assets.length === 0
            ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}><div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>◈</div><div>No media assets yet</div></div>
            : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {assets.map(a => <AssetCard key={a.id} asset={a} onSelect={setSelected} onDelete={handleDelete} />)}
              </div>
      )}

      {/* List */}
      {view === 'list' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['', 'Name / Alt Text', 'Size', 'Dimensions', 'Uploaded', ''].map((c, i) => (
                <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{c}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                : assets.map(a => (
                  <tr key={a.id} onClick={() => setSelected(a)}
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                    {/* Thumbnail cell */}
                    <td style={{ padding: '8px 8px 8px 16px', width: 60 }}>
                      <ListThumb asset={a} />
                    </td>

                    {/* Name cell */}
                    <td style={{ padding: '10px 16px', maxWidth: 260 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.alt_text || a.filename}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <span style={{ fontSize: 10, color: TYPE_COLOR[a.type], background: TYPE_COLOR[a.type] + '20', padding: '1px 6px', borderRadius: 8, fontWeight: 600, textTransform: 'uppercase' }}>{a.type}</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{a.filename}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatSize(a.size_bytes)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{a.width ? `${a.width}×${a.height}` : '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={e => { e.stopPropagation(); handleDelete(a); }} style={{ fontSize: 12, color: 'var(--red-light)', background: 'none', border: 'none', cursor: 'pointer' }}>Delete</button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          {[...Array(Math.min(pagination.pages, 8))].map((_, i) => {
            const p = i + 1;
            return (
              <button key={p} onClick={() => setFilter(f => ({ ...f, page: p }))} style={{ width: 30, height: 30, borderRadius: 4, fontSize: 12, cursor: 'pointer', background: p === filter.page ? 'var(--accent)' : 'var(--bg-elevated)', color: p === filter.page ? '#0d0f12' : 'var(--text-secondary)', border: `1px solid ${p === filter.page ? 'var(--accent)' : 'var(--border)'}`, fontWeight: p === filter.page ? 600 : 400 }}>{p}</button>
            );
          })}
        </div>
      )}

      {selected && <AssetDetail asset={selected} onClose={() => setSelected(null)} onDelete={handleDelete} onUpdate={handleUpdate} />}
    </div>
  );
}